import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';

import { useAuth } from 'context/auth';
import { EventContext } from 'context/event';
import { cn } from 'lib/utils';

import type { ReportView } from './api/reportViewTypes';
import {
    applyReportLibrarySectionListOrder,
    applyReportLibrarySectionOrders,
} from './applyReportLibraryOrder';
import { useReportLibrary } from './hooks/useReportLibrary';
import { useReportViews } from './hooks/useReportViews';
import { buildReportLibrarySections, findReportById, FAVORITES_SECTION_ID } from './mock/reports';
import { getStoredFavoriteReportIds, setStoredFavoriteReportIds } from './reportFavoritesStorage';
import {
    getStoredReportLibrarySectionIds,
    getStoredReportLibrarySectionOrders,
    setStoredReportLibrarySectionIds,
    setStoredReportLibrarySectionOrders,
} from './reportLibraryOrderStorage';
import {
    getStoredLibrarySidebarCollapsed,
    setStoredLibrarySidebarCollapsed,
} from './reportLibrarySidebarStorage';
import { toReportDateRangeValue } from './reportQueryParams';
import { applyReportViewConfigToParams } from './reportViewConfig';
import { ReportLibrary } from './ReportLibrary';
import { ReportViewer } from './ReportViewer';
import { useReportQueryParams } from './useReportQueryParams';
import './reports-theme.css';

const EMPTY_VIEWS: ReportView[] = [];

export default function Reports() {
    const event = useContext(EventContext);
    const { auth } = useAuth();
    const {
        params,
        searchInput,
        setReport,
        setRange,
        setSearch,
        setSort,
        setPage,
        setLimit,
        setTickets,
        replaceParams,
    } = useReportQueryParams(auth?.userId);
    const [isLibraryCollapsed, setIsLibraryCollapsed] = useState(() =>
        getStoredLibrarySidebarCollapsed(),
    );
    const [favoriteReportIds, setFavoriteReportIds] = useState<string[]>(() =>
        getStoredFavoriteReportIds(),
    );
    const [sectionOrders, setSectionOrders] = useState(() => getStoredReportLibrarySectionOrders());
    const [sectionIds, setSectionIds] = useState(() => getStoredReportLibrarySectionIds());

    const {
        data: library,
        isError: isLibraryError,
        isPending: isLibraryPending,
    } = useReportLibrary(event?.event_id);

    const { data: viewsData, isFetching: isViewsFetching } = useReportViews(event?.event_id);
    const views = viewsData?.views ?? EMPTY_VIEWS;

    const sections = library?.sections ?? null;
    const defaultReportId = library?.defaultReportId ?? null;

    const selectedView = useMemo(() => {
        if (!params.view) {
            return null;
        }

        return views.find((view) => view.eventReportViewId === params.view) ?? null;
    }, [params.view, views]);

    const myViews = useMemo(() => {
        if (!auth?.userId) {
            return [];
        }

        return views.filter((view) => String(view.ownerUserId) === String(auth.userId));
    }, [auth?.userId, views]);

    const sharedViews = useMemo(() => {
        if (!auth?.userId) {
            return [];
        }

        return views.filter(
            (view) =>
                String(view.ownerUserId) !== String(auth.userId) &&
                (view.visibility === 'team' || view.visibility === 'link'),
        );
    }, [auth?.userId, views]);

    const toggleLibraryCollapse = useCallback(() => {
        setIsLibraryCollapsed((previous) => {
            const next = !previous;
            setStoredLibrarySidebarCollapsed(next);
            return next;
        });
    }, []);

    const toggleFavorite = useCallback((reportId: string) => {
        setFavoriteReportIds((previous) => {
            const next = previous.includes(reportId)
                ? previous.filter((id) => id !== reportId)
                : [...previous, reportId];
            setStoredFavoriteReportIds(next);
            return next;
        });
    }, []);

    const reorderSection = useCallback((sectionId: string, orderedIds: string[]) => {
        if (sectionId === FAVORITES_SECTION_ID) {
            setFavoriteReportIds(orderedIds);
            setStoredFavoriteReportIds(orderedIds);
            return;
        }

        setSectionOrders((previous) => {
            const next = { ...previous, [sectionId]: orderedIds };
            setStoredReportLibrarySectionOrders(next);
            return next;
        });
    }, []);

    const reorderSections = useCallback((orderedSectionIds: string[]) => {
        const next = orderedSectionIds.filter((id) => id !== FAVORITES_SECTION_ID);
        setSectionIds(next);
        setStoredReportLibrarySectionIds(next);
    }, []);

    const displaySections = useMemo(() => {
        if (!sections) {
            return null;
        }

        return applyReportLibrarySectionListOrder(
            applyReportLibrarySectionOrders(
                buildReportLibrarySections(sections, favoriteReportIds),
                sectionOrders,
            ),
            sectionIds,
        );
    }, [sections, favoriteReportIds, sectionOrders, sectionIds]);

    const selectView = useCallback(
        (view: ReportView) => {
            replaceParams(
                applyReportViewConfigToParams(
                    view.baseReportId,
                    view.eventReportViewId,
                    view.config,
                ),
            );
        },
        [replaceParams],
    );

    useEffect(() => {
        if (!selectedView) {
            return;
        }

        // Hydrate URL/state from the saved view whenever the selected view id changes.
        replaceParams(
            applyReportViewConfigToParams(
                selectedView.baseReportId,
                selectedView.eventReportViewId,
                selectedView.config,
            ),
        );
        // Only re-hydrate when switching to a different saved view.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedView?.eventReportViewId]);

    useEffect(() => {
        if (!sections || !defaultReportId) {
            return;
        }

        if (params.view) {
            // Wait until views have settled before treating an unknown view id as invalid.
            // Otherwise create/fork can briefly miss the cache and reset back to the report.
            if (viewsData && !isViewsFetching && !selectedView) {
                setReport(defaultReportId);
            }
            return;
        }

        if (!findReportById(sections, params.report)) {
            setReport(defaultReportId);
        }
    }, [
        sections,
        defaultReportId,
        params.report,
        params.view,
        selectedView,
        viewsData,
        isViewsFetching,
        setReport,
    ]);

    const selectedReport = sections
        ? findReportById(sections, selectedView?.baseReportId ?? params.report)
        : undefined;
    const eventName = event?.name || 'Current Event';
    const dateRange = useMemo(
        () =>
            toReportDateRangeValue({
                range: params.range,
                customStart: params.customStart,
                customEnd: params.customEnd,
            }),
        [params.range, params.customStart, params.customEnd],
    );

    if (isLibraryError) {
        return (
            <Box display="flex" flexDirection="column" height="100%">
                <Box px={[2, 4]} pt={[2, 4]} pb={6} flexGrow={1}>
                    <div className="reports-light-theme mx-auto w-full max-w-[1400px]">
                        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                            Unable to load report library.
                        </div>
                    </div>
                </Box>
            </Box>
        );
    }

    if (isLibraryPending || !sections || !selectedReport) {
        return null;
    }

    return (
        <div className="reports-light-theme flex h-full min-h-0 w-full flex-col">
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row">
                <div
                    className={cn(
                        'flex min-h-0 shrink-0 overflow-hidden transition-[width] duration-300 ease-in-out',
                        isLibraryCollapsed ? 'w-full lg:w-[52px]' : 'w-full lg:w-[320px]',
                    )}
                >
                    <ReportLibrary
                        sections={displaySections ?? sections}
                        selectedReportId={params.report}
                        selectedViewId={params.view}
                        myViews={myViews}
                        sharedViews={sharedViews}
                        onSelectReport={setReport}
                        onSelectView={selectView}
                        onToggleFavorite={toggleFavorite}
                        onReorderSection={reorderSection}
                        onReorderSections={reorderSections}
                        isCollapsed={isLibraryCollapsed}
                        onToggleCollapse={toggleLibraryCollapse}
                    />
                </div>
                <div className="m-4 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden transition-[margin] duration-300 ease-in-out">
                    <ReportViewer
                        report={selectedReport}
                        eventName={eventName}
                        dateRange={dateRange}
                        onDateRangeChange={setRange}
                        searchValue={searchInput}
                        onSearchChange={setSearch}
                        sorting={params.sort}
                        onSortingChange={setSort}
                        page={params.page}
                        limit={params.limit}
                        onPageChange={setPage}
                        onLimitChange={setLimit}
                        ticketIds={params.tickets}
                        onTicketIdsChange={setTickets}
                        selectedView={selectedView}
                        myViews={myViews}
                        onApplyViewParams={replaceParams}
                    />
                </div>
            </div>
        </div>
    );
}
